<template>
  <div class="h5-container">
    <!-- 顶部导航栏 -->
    <van-nav-bar
        class="top-bar"
        title="个人中心"
        left-text="返回"
        left-arrow
        @click-left="onClickLeft"
    ></van-nav-bar>
    <div class="container-item">
      <!-- 用户信息卡片：使用 van-card 实现 -->
      <van-card shadow="never" class="user-card">
        <template #desc>
          <div class="user-header">
            <!-- 头像区域：添加点击事件 + 编辑图标 -->
            <div class="avatar-wrap" @click="openAvatarUpload">
              <van-image
                  round
                  :src="avatarPic"
                  :loading="uploadLoading"
                  fit="cover"
                  class="user-header-img"
              ></van-image>
              <!-- 头像编辑图标（固定显示在头像右下角） -->
              <div class="avatar-edit-icon">
                <van-icon name="edit" size="12" color="#fff"/>
              </div>
            </div>
            <div class="user-info">
              <h3>{{ userInfo.nickName || '未登录' }}</h3>
              <h3>{{ userInfo.userName ? desensitizePhone(userInfo.userName) : '**** **** ****' }}</h3>
            </div>
            <van-button type="primary" size="mini">注册{{ userInfo.registerDay || 0 }}天</van-button>
          </div>
        </template>
      </van-card>

      <!-- 隐藏的上传组件：确保ref能被正确获取 -->
      <van-uploader
          ref="avatarUploader"
          v-model="avatarFileList"
          accept="image/*"
          :multiple="false"
          :max-count="1"
          :after-read="handleAvatarUpload"
          style="display: none"
      />

      <!-- 功能列表 -->
      <div class="function-list">
        <!-- 第一组功能：球杆相关 -->
        <van-cell-group inset class="function-group">
          <van-cell
              title="昵称"
              :value="userInfo.nickName"
              class="function-item"
          />
          <van-cell
              title="性别"
              :value="userInfo.sex === '0' ? '男' : (userInfo.sex === '1' ? '女' : '未知')"
              class="function-item"
          />
          <van-cell
              title="注册日期"
              :value="userInfo?.createTime?.substring(0, 10) ?? '--'"
              class="function-item"
          />
          <van-cell
              title="个性签名"
              :value="userInfo.signature"
              class="function-item"
          />
          <van-cell
              title="联系地址"
              :value="userInfo.address"
              class="function-item"
          />
          <van-cell
              v-if="userInfo.userType === '02'"
              icon="qr"
              title="门店注册二维码"
              is-link
              @click="showQrCode = true"
              class="function-item"
          />
        </van-cell-group>

        <!-- 第二组功能：个人设置 -->
        <van-cell-group inset class="function-group">
          <van-cell
              icon="records-o"
              title="修改密码"
              is-link
              @click="toMyCue"
              class="function-item"
          />
          <van-cell
              icon="user-o"
              title="修改资料"
              is-link
              @click="toEditInfo"
              class="function-item"
          />
        </van-cell-group>
      </div>

      <!-- 退出登录按钮 -->
      <div class="logout-wrap">
        <van-button
            type="default"
            round
            block
            class="logout-btn"
            @click="handleLogout"
        >
          退出登录
        </van-button>
      </div>

      <!-- 门店注册二维码弹窗 -->
      <van-popup v-model="showQrCode" round closeable close-icon-position="top-right" :style="{ width: '80%' }">
        <div class="qr-code-wrap">
          <div class="qr-code-title">门店注册二维码</div>
          <van-image
              v-if="userInfo.qrVo && userInfo.qrVo.url"
              :src="userInfo.qrVo.url"
              fit="contain"
              class="qr-code-img"
          />
          <div v-else class="qr-code-empty">暂无二维码</div>
        </div>
      </van-popup>
    </div>
  </div>
</template>

<script>
import '@/assets/css/mobileGlobal.scss';
// 仅保留需要的工具组件（全局引入Vant后，无需导入VanUploader/VanImage等）
import {Dialog, Toast} from 'vant';
import defaultAvatar from '@/assets/img/h5/avatar-default.png';
import {logout, memberAvatar, memberInformation} from "@/api/mine";
import {removeTokenCookie} from '@/utils/cookie';
// 如果项目用Vuex存储了Token，补充导入mapActions（如果没用到可删除）
import {mapActions} from 'vuex';

export default {
  name: 'PersonalCenter',
  // 全局引入Vant后，移除components注册
  data() {
    return {
      defaultAvatar: defaultAvatar,
      userInfo: {},
      avatarPic: '',
      avatarFileList: [], // 上传文件列表
      uploadLoading: false, // 上传中loading状态
      showQrCode: false, // 门店注册二维码弹窗显隐
      compressOptions: {
        quality: 0.8,
        width: 200,
        height: 200
      }
    };
  },
  created() {
    this.getUserInfo();
  },
  // 如果用Vuex存储Token，添加mapActions（没用到可删除）
  methods: {
    ...mapActions(['setToken']), // 用于清空Vuex中的Token（如果有）

    // 手机号脱敏处理
    desensitizePhone(phone) {
      if (!phone || phone.length !== 11) return phone;
      return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
    },

    getUserInfo() {
      memberInformation().then(res => {
        if (res.code === 200 && res.data) {
          this.userInfo = res.data;

          if (this.userInfo && this.userInfo.avatar) {
            this.avatarPic = this.userInfo.avatar.url || defaultAvatar;
          } else {
            this.avatarPic = defaultAvatar;
          }
        } else if (res.code !== 401) {
          Dialog.alert({title: '提示', message: res.returnMsg});
        }
      }).catch(err => {
        Dialog.alert({title: '错误', message: '请稍后重试'});
      });
    },

    // 1. 打开头像选择器（增加ref容错）
    openAvatarUpload() {
      if (!this.userInfo.nickName || this.userInfo.nickName === '未登录') {
        Toast('请先登录后修改头像');
        this.$router.push('/app_login');
        return;
      }
      // 修复：确保能获取到uploader组件
      const uploader = this.$refs.avatarUploader;
      if (!uploader) {
        Toast('上传组件加载失败，请刷新页面');
        return;
      }
      const fileInput = uploader.$el.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.click();
      } else {
        Toast('文件选择器初始化失败');
      }
    },

    // 2. 图片选择前的预处理（核心修复：返回true允许上传）
    beforeAvatarRead(file) {
      // 限制文件类型
      if (!file.type.startsWith('image/')) {
        Toast('请选择图片格式的文件');
        return false;
      }
      // 限制文件大小（5M）
      if (file.size > 5 * 1024 * 1024) {
        Toast('图片大小不能超过5MB');
        return false;
      }
      // 注释压缩后，必须返回true！否则会阻止上传流程
      return true;
    },

    // 3. 处理头像上传（完善日志+调试）
    async handleAvatarUpload(file) {
      try {
        this.uploadLoading = true;
        // 调试：打印文件信息，确认文件存在
        console.log('上传的文件信息：', file.file);

        // 组装FormData
        const formData = new FormData();
        formData.append('file', file.file); // 确认后端接收字段名是file

        // 调试：遍历打印FormData内容（FormData无法直接console.log）
        for (let [key, value] of formData.entries()) {
          console.log(`FormData: ${key} =`, value);
        }

        // 调用上传接口
        const res = await memberAvatar(formData);
        console.log('接口返回结果：', res); // 调试：打印接口返回

        if (res.code === 200) {
          // 适配后端返回格式（根据实际返回调整字段名）
          const newAvatarUrl = res.data?.avatarUrl || res.data;
          if (newAvatarUrl) {
            this.userInfo.avatar = newAvatarUrl;
            Toast('头像修改成功');
            // 可选：重新拉取用户信息，确保数据同步
            // this.getUserInfo();
          } else {
            Toast('接口未返回头像地址');
          }
        } else {
          Toast(res.returnMsg || '头像上传失败');
        }
      } catch (error) {
        // 详细打印错误，方便定位
        console.error('头像上传异常详情：', error);
        Toast(`上传失败：${error.message || '网络异常'}`);
      } finally {
        this.uploadLoading = false;
        this.avatarFileList = []; // 清空上传列表
      }
    },

    // 返回上一页
    onClickLeft() {
      this.$router.go(-1);
    },

    // 跳转：修改密码
    toMyCue() {
      this.$router.push('/edit_pass');
    },

    // 跳转：修改资料
    toEditInfo() {
      this.$router.push('/app_edit_info');
    },

    // 退出登录
    handleLogout() {

       let loginPath = '/app_login';
       if(this.userInfo && this.userInfo.userType === '03') {
         loginPath = '/sale_login';
       }
      Dialog.confirm({
        title: '确认退出',
        message: '是否确认退出当前账号？'
      }).then(() => {
        logout().then(res => {
          if (res.code === 200) {
            Toast('退出成功');
            // 🔴 关键修改2：清除Cookie/localStorage中的Token
            removeTokenCookie();
            // 可选：如果Vuex中存储了Token，清空它（没用到可删除）
            this.setToken('');
            // 跳转登录页
            this.$router.replace(loginPath);
          } else {
            Dialog.alert({
              title: '提示',
              message: res.returnMsg,
            }).then(() => {
            });
          }
        }).catch(err => {
          removeTokenCookie();
          this.setToken(''); // 可选：清空Vuex Token
          Toast('退出失败，已清除本地Token，请重新登录');
          this.$router.replace(loginPath);
        })
      }).catch(() => {
      });
    }
  },
};
</script>

<style scoped lang="scss">
::v-deep .van-card {
  @media (min-width: 768px) {
    padding: 20px 80px;
  }
}
::v-deep .van-card__content {
  min-height: 0;
}
::v-deep .van-cell-group--inset{
    margin: 0;
}

// 全局容器
.h5-container {
  min-height: 100vh;
  background-color: #1A1A1A;
  padding: 0 16px;
  padding-bottom: 30px;
  box-sizing: border-box;
}

// 顶部导航栏
.top-bar {
  background: linear-gradient(135deg, #D4AF37 0%, #B8860B 100%);
  margin: 0 -16px; // 全屏导航栏
  ::v-deep .van-nav-bar__title {
    color: #0f0f0f;
    font-weight: bold;
  }

  ::v-deep .van-icon-arrow-left,
  ::v-deep .van-nav-bar__text {
    color: #0f0f0f !important;
  }
}

.container-item {
  padding: 0 16px;
}

.user-card {
  margin: 16px 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #222 0%, #000 100%);
  color: #fff;
  @media (min-width: 768px) {
    border-radius: 12px;
    margin: 16px 0;
  }
}

.user-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px;
  @media (min-width: 768px) {
    margin: auto 0;
  }

  h3 {
    margin-bottom: 4px;
    font-size: 14px;
    color: #fff;
    font-weight: normal;
    @media (min-width: 768px) {
      font-size: 24px;
    }
    margin: 0; // 重置默认margin
    &:nth-child(2) {
      font-size: 12px;
      color: #999;
      margin-top: 4px;
      @media (min-width: 768px) {
        font-size: 24px;
      }
    }
  }
}

.user-header-img {
  width: 48px;
  height: 48px;
  @media (min-width: 768px) {
    width: 100px;
    height: 100px;
  }
}

// 头像区域样式
.avatar-wrap {
  position: relative;
  cursor: pointer; // 提示可点击
  &:hover .avatar-edit-icon {
    opacity: 1;
  }
}

// 头像编辑图标样式
.avatar-edit-icon {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 16px;
  background-color: rgba(212, 175, 55, 0.9);
  @media (min-width: 768px) {
    width: 20px;
    height: 20px;
    bottom: 15px;
  }
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;
  border: 1px solid #fff;
}

.user-info {
  flex: 1;
  margin: 0 12px;
  @media (min-width: 768px) {
    margin: 0 30px;
  }
}

.stats {
  display: flex;
  justify-content: space-around;
  padding-top: 16px;
  border-top: 1px solid rgba(#D4AF37, 0.2);

  .stat-item {
    text-align: center;

    .value {
      margin: 0;
      font-size: 16px;
      color: #D4AF37;
    }

    .label {
      margin: 0;
      font-size: 12px;
      color: #fff;
    }
  }
}
::v-deep .van-cell {
  @media (min-width: 768px) {
    font-size: 24px;
    line-height:42px;
    padding: 10px 30px;
  }
}
::v-deep .van-cell__left-icon{
  @media (min-width: 768px) {
    height: 42px;
    font-size: 42px;
    line-height: 42px;
  }
}

::v-deep .van-cell__right-icon{
  @media (min-width: 768px) {
    height: 42px;
    font-size: 24px;
    line-height: 42px;
  }
}
::v-deep .van-button{
  @media (min-width: 768px) {
    height: 50px;
    font-size: 20px;
  }
}

// 功能列表
.function-list {
  .function-group {
    background-color: rgba(26, 26, 26, 0.7);
    border-radius: 12px;
    margin-bottom: 15px;
    @media (min-width: 768px) {
      border-radius: 12px;
      margin-bottom: 25px;
    }

    ::v-deep .van-cell-group__inset {
      border-radius: 12px;
    }
  }

  .function-item {
    ::v-deep .van-cell {
      background-color: transparent;
      color: #fff;
      border-bottom: 1px solid rgba(212, 175, 55, 0.1);

      &:last-child {
        border-bottom: none;
      }

      .van-cell__title {
        color: #fff;
        font-size: 16px;
      }

      .van-icon {
        color: #D4AF37;
        font-size: 18px;
      }

      .van-cell__right-icon {
        color: #999;
      }
    }
  }
}


// 退出登录按钮
.logout-wrap {
  margin-top: 20px;
  @media (min-width: 768px) {
    margin-top: 20px;
  }

  .logout-btn {
    height: 48px;
    font-size: 16px;
    @media (min-width: 768px) {
      height: 70px;
      font-size: 24px;
    }
    color: #f56c6c;
    border: 1px solid #f56c6c;
    background-color: transparent;

    &:active {
      background-color: rgba(245, 108, 108, 0.1);
    }
  }
}

// 门店注册二维码弹窗
.qr-code-wrap {
  padding: 30px 20px 20px;
  text-align: center;

  .qr-code-title {
    font-size: 16px;
    font-weight: bold;
    color: #222;
    margin-bottom: 16px;
    @media (min-width: 768px) {
      font-size: 24px;
      margin-bottom: 24px;
    }
  }

  .qr-code-img {
    width: 100%;
    max-width: 260px;
    @media (min-width: 768px) {
      max-width: 400px;
    }
  }

  .qr-code-empty {
    color: #999;
    font-size: 14px;
    padding: 40px 0;
    @media (min-width: 768px) {
      font-size: 20px;
      padding: 60px 0;
    }
  }
}
</style>